import { Component, inject, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ReportLabelsService } from './report-labels.service';
import { ReportLabel, ReportLabelPayload } from './report-labels.model';
import { OrganizationDetailModel } from '../../organizations/organization.interface';
import { NzTableModule } from 'ng-zorro-antd/table';
import { SvgIconComponent } from 'angular-svg-icon';
import { CommonModule } from '@angular/common';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { FormsModule } from '@angular/forms';
import { CommonService } from '../../../shared/services/common.service';
import { ErrorResponse } from '../../../core/models/error.models';
import { OrganizationSelectionService } from '../../../core/services/organization-selection.service';
import { DRAWER_IDS } from '../../../core/constants/drawer-ids.constant';
import { ManageOrganizationDrawerParams } from '../../manage-organization/manage-organization.interface';
import { DrawerService } from '../../../core/services/drawer.service';
import { OrgManagementMode } from '../../manage-organization/manage-organization.enums';
import { AuditContextService } from '../../audit-logs/audit-context.service';
import { AuditFieldChange } from '../../audit-logs/audit-logs.interface';

@Component({
  selector: 'app-report-labels',
  standalone: true,
  imports: [
    NzTableModule,
    SvgIconComponent,
    CommonModule,
    NzInputModule,
    NzButtonModule,
    FormsModule,
  ],
  templateUrl: './report-labels.component.html',
  styleUrls: ['./report-labels.component.scss'],
})
export class ReportLabelsComponent implements OnInit {
  private readonly commonService = inject(CommonService);
  private readonly auditContextService = inject(AuditContextService);

  loading$: Observable<boolean>;
  reportLabels$: Observable<ReportLabel[]>;

  private readonly destroy$ = new Subject<void>();

  currentEditingItem: ReportLabel | null = null;

  orgData!: OrganizationDetailModel;
  organizationId!: number;

  TABLE_COLUMNS = [
    {
      title: 'Token',
      key: 'labelKey',
    },
    {
      title: 'Token Value',
      key: 'overrideValue',
    },
    {
      title: 'Action',
      key: 'action',
    },
  ];
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.VIEW,
    organizationName: '',
  };

  constructor(
    private readonly reportLabelsService: ReportLabelsService,
    private readonly orgSelectionService: OrganizationSelectionService,
    private readonly drawerService: DrawerService
  ) {
    this.loading$ = this.reportLabelsService.loading$;
    this.reportLabels$ = this.reportLabelsService.reportLabels$;
  }

  ngOnInit(): void {
    this.setupDrawerParams();
    this.subscribeToOrgData();
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  private subscribeToOrgData(): void {
    this.orgSelectionService.orgId$
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe(res => {
        if (res) {
          this.organizationId = res;
          this.reportLabelsService.loadReportLabels(this.organizationId);
        } else if (this.orgParams.organizationId) {
          const orgId = Number(this.orgParams.organizationId);
          this.organizationId = orgId;
          this.orgData = {
            organizationId: orgId,
          } as OrganizationDetailModel;
          this.reportLabelsService.loadReportLabels(this.organizationId);
        }
      });
  }

  toggleEdit(item: ReportLabel): void {
    if (this.currentEditingItem && this.currentEditingItem !== item) {
      this.currentEditingItem.isEditing = false;
      this.currentEditingItem.overrideValue =
        this.currentEditingItem.originalValue ?? '';
    }

    this.currentEditingItem = item;
    item.isEditing = true;
    item.originalValue = item.overrideValue;
  }

  cancelEdit(item: ReportLabel): void {
    item.isEditing = false;
    item.overrideValue = item.originalValue ?? '';
    this.currentEditingItem = null;
  }

  saveChanges(item: ReportLabel): void {
    if (item.overrideValue === item.originalValue) {
      item.isEditing = false;
    } else {
      item.isEditing = false;
      const reportLabel: ReportLabelPayload = {
        id: item.id,
        organizationId: this.organizationId,
        labelKeyId: item.labelKeyId,
        overrideValue: item.overrideValue,
        _modifiedFields: this.buildReportLabelModifiedFields(item),
      };
      this.reportLabelsService.createReportLabel(reportLabel).subscribe({
        next: () => {
          this.commonService.notify('success', 'Data saved successfully');
          this.reportLabelsService.loadReportLabels(this.organizationId);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
    }

    this.currentEditingItem = null;
  }

  private buildReportLabelModifiedFields(
    item: ReportLabel
  ): AuditFieldChange[] {
    const original = this.getEnrichedReportLabelAuditPayload(
      item,
      item.originalValue ?? ''
    );
    const updated = this.getEnrichedReportLabelAuditPayload(
      item,
      item.overrideValue
    );

    const rawChanges = this.auditContextService.buildFieldChanges(
      original,
      updated,
      {
        sensitiveFields: [],
        labelMap: this.getReportLabelAuditLabelMap(),
        excludeFields: ['id', 'organizationId', 'labelKeyId'],
      }
    );

    return rawChanges
      .filter(change => change.oldValue !== change.newValue)
      .map(change => {
        if (change.field === 'overrideValue') {
          return {
            ...change,
            fieldLabel: this.getOverrideValueFieldLabel(item),
          };
        }

        return change;
      });
  }

  private getReportLabelAuditLabelMap(): Record<string, string> {
    return {
      organizationId: 'Organization',
      labelKey: 'Token',
      overrideValue: 'Token Value',
    };
  }

  private getEnrichedReportLabelAuditPayload(
    item: ReportLabel,
    overrideValue: string
  ): Record<string, unknown> {
    return {
      id: item.id,
      organizationId: this.getOrganizationNameForAudit(),
      labelKeyId: item.labelKeyId,
      labelKey: item.labelKey || item.labelKeyId.toString(),
      overrideValue: this.normalizeAuditValue(overrideValue),
    };
  }

  private getOverrideValueFieldLabel(item: ReportLabel): string {
    const token = (item.labelKey || '').trim();
    return token ? `${token} Token Value` : 'Token Value';
  }

  private normalizeAuditValue(value: string | null | undefined): string {
    return (value || '').replaceAll(/\s+/g, ' ').trim();
  }

  private getOrganizationNameForAudit(): string {
    const organizationName = this.orgParams.organizationName || '';

    if (organizationName.trim()) {
      return organizationName.trim();
    }

    return this.organizationId ? this.organizationId.toString() : '';
  }

  getCurrentLength(data: ReportLabel): number {
    return data.overrideValue?.length || 0;
  }

  checkCharacterCount(data: ReportLabel): void {
    const currentLength = this.getCurrentLength(data);
    if (data.length && currentLength > data.length) {
      data.overrideValue = data.overrideValue?.substring(0, data.length);
    }
  }
}
